import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@xantory/store';
import { api, throwFromResponse } from '../../core/client';
import { resolvePath } from '../../core/path';
import type { PlanTelemetry } from './plan-telemetry.types';

// Site-scoped `/v1/plan-telemetry/*` — cloud prefixes `/sites/{id}` via
// resolvePath (backend D-060: raw signals stay at the farm and the console
// drills in live, so there is no cloud copy of this to read instead).
const BASE = '/v1/plan-telemetry';
const KEY = ['plan-telemetry'] as const;

export interface TelemetryWindow {
  fromMs?: number;
  toMs?: number;
}

export interface TelemetryOptions extends TelemetryWindow {
  /** Omitted ⇒ the server picks by span, using the ONE platform bucket policy. */
  bucket?: 'raw' | 'minute' | 'hour' | 'day';
  /** Omitted ⇒ every parameter this crop's profile states a band for. */
  parameters?: string[];
}

/** Backend R-372 — the exact bucket widths the export accepts (`INTERVALS` in
 *  gateway-local `plan_telemetry/v1/telemetry.rs`). ⚠ Hand-mirrored: a width
 *  listed here and not there is a 400, never a silently different file. */
export const EXPORT_INTERVALS = ['1m', '5m', '15m', '30m', '1h', '6h', '12h', '1d'] as const;
export type ExportInterval = (typeof EXPORT_INTERVALS)[number];

/** Backend R-372 — how each row's time is written. `ms` is epoch milliseconds
 *  (column `ts_ms`, the pre-R-372 file); `utc` is `2026-09-15 13:33:20` (column
 *  `time_utc`), which a spreadsheet reads as a date. */
export type ExportTimeFormat = 'ms' | 'utc';

export interface TelemetryExportOptions extends TelemetryOptions {
  /** Omitted ⇒ the same automatic resolution the chart uses. Excludes `bucket`. */
  interval?: ExportInterval;
  timeFormat?: ExportTimeFormat;
}

function params(batchId: string, o: TelemetryExportOptions): URLSearchParams {
  const q = new URLSearchParams({ batch_id: batchId });
  if (o.fromMs !== undefined) q.set('from', String(o.fromMs));
  if (o.toMs !== undefined) q.set('to', String(o.toMs));
  // The backend refuses both together — an interval IS the resolution.
  if (o.interval) q.set('interval', o.interval);
  else if (o.bucket) q.set('bucket', o.bucket);
  if (o.parameters?.length) q.set('parameters', o.parameters.join(','));
  if (o.timeFormat) q.set('time_format', o.timeFormat);
  return q;
}

/**
 * One crop's readings, its band ladder over time, and every excursion — for the
 * rooms and racks it is actually growing in.
 *
 * Addressed by BATCH, not by plan: a plan mints one batch per grow room
 * (backend R-321) and `PlanDto.batch_ids` carries them. A plan with no batches
 * has not been activated, so there is nothing to chart and the query stays off.
 */
export function usePlanTelemetry(batchId: string, options: TelemetryOptions = {}) {
  return useQuery({
    queryKey: [
      ...KEY,
      batchId,
      options.fromMs ?? 'cycle',
      options.toMs ?? 'now',
      options.bucket ?? 'auto',
      (options.parameters ?? []).join(',') || 'all',
    ],
    queryFn: () => api.get<PlanTelemetry>(`${BASE}/series?${params(batchId, options)}`),
    enabled: Boolean(batchId),
    // ⚠ HOLD THE PREVIOUS WINDOW while the next one loads.
    //
    // Without this, changing the window (or dragging a range) empties `data`,
    // every chart unmounts, and the whole section collapses to a spinner before
    // reappearing — which reads as the PAGE reloading rather than one chart
    // updating. It also blanks the Room/Rack selects mid-flight, because their
    // options come from the same payload.
    //
    // With it the charts keep their last render and simply swap their points in
    // when the new window arrives; the caller dims them via `isFetching` so the
    // staleness is still visible.
    placeholderData: keepPreviousData,
    // A running crop's chart goes stale; a finished one does not. 30 s is the
    // bell's cadence and is well inside the backend's own 20 s series cache, so
    // this costs a Redis hit rather than a Timescale scan.
    staleTime: 30_000,
  });
}

/**
 * Download the SELECTED window as CSV — the same rows the chart is drawing,
 * each carrying the band in force at that instant and the zone the classifier
 * put it in, so the file explains itself away from this screen.
 *
 * A plain fetch rather than `api.get`: the response is a file, not JSON, and it
 * must reach the browser as a download. The auth header and the site-scoped
 * path rewrite are applied by hand for exactly that reason — they are the two
 * things `api.get` would otherwise have done.
 */
export async function downloadPlanTelemetryCsv(
  batchId: string,
  options: TelemetryExportOptions = {},
): Promise<{ blob: Blob; filename: string; truncated: boolean }> {
  const path = resolvePath(`${BASE}/export.csv?${params(batchId, options)}`);
  const { accessToken } = useAuthStore.getState();
  const res = await fetch(path, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  // The server's own message, not the bare status: a refused interval says
  // which window and which limit, and that is the operator's fix.
  if (!res.ok) await throwFromResponse(res);
  // The backend names the file after the crop and the exact window, so two
  // exports of different ranges never overwrite each other in Downloads.
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const named = /filename="([^"]+)"/.exec(disposition)?.[1];
  return {
    blob: await res.blob(),
    filename: named ?? `crop-${batchId}.csv`,
    // ⚠ Carried in a HEADER because the body is a file: a reader must be able to
    // tell a short window from a clipped one, and a CSV outlives the screen.
    truncated: res.headers.get('X-Series-Truncated') === 'true',
  };
}
